import { $, bindClick } from "../lib/dom.js";
import { escHtml } from "../lib/hints.js";
import { apiPublic } from "../api.js";

const GENDER_ZH = { female: "女", male: "男" };

let cache = null;
let filterL1 = "";
let filterL2 = "";
let searchText = "";
let selectedCid = "";

function notify(text, ok) {
  const el = $("key-banner");
  if (!el) return;
  el.textContent = text;
  el.classList.toggle("hidden", !text);
  el.classList.toggle("ok", !!ok);
}

function matchesSearch(hay) {
  const q = searchText.trim().toLowerCase();
  if (!q) return true;
  return String(hay || "").toLowerCase().includes(q);
}

function nodes() {
  return (cache && (cache.items || cache.nodes || [])) || [];
}

function nodeCid(n) {
  return String(n.cid || "");
}

function nodePath(n) {
  return String(n.path || n.title || "");
}

function nodeL1(n) {
  if (n.l1) return String(n.l1);
  return nodePath(n).split(">")[0] || "";
}

function nodeL2(n) {
  if (n.l2) return String(n.l2);
  const parts = nodePath(n).split(">").map((s) => s.trim()).filter(Boolean);
  return parts[1] || "";
}

function filteredNodes() {
  return nodes().filter((n) => {
    if (filterL1 && nodeL1(n) !== filterL1) return false;
    if (filterL2 && nodeL2(n) !== filterL2) return false;
    const hay = [nodeCid(n), nodePath(n), n.name].join(" ");
    return matchesSearch(hay);
  });
}

function renderMeta() {
  const el = $("pcat-meta");
  if (!el || !cache) return;
  const ver = cache.version || "—";
  const at = cache.updated_at || "—";
  const leaf = cache.total != null ? cache.total : nodes().length;
  el.textContent = `权威 ${cache.authority || "platform_federation"} · 词表 v${ver} · 更新 ${at} · ${leaf} 个淘宝叶子`;
}

function renderLevelFilters() {
  const s1 = $("pcat-filter-l1");
  const s2 = $("pcat-filter-l2");
  if (!s1 || !s2 || !cache) return;
  const opt = cache.level_options || {};
  const l1s = opt.l1 && opt.l1.length ? opt.l1 : [...new Set(nodes().map(nodeL1).filter(Boolean))].sort();
  const l2s = nodes()
    .filter((n) => !filterL1 || nodeL1(n) === filterL1)
    .map(nodeL2)
    .filter(Boolean);
  const uniq2 = [...new Set(l2s)].sort();
  if (!s1.dataset.ready) {
    s1.innerHTML =
      '<option value="">全部一级</option>' +
      l1s.map((v) => `<option value="${escHtml(v)}">${escHtml(v)}</option>`).join("");
    s1.dataset.ready = "1";
  }
  s1.value = filterL1;
  s2.innerHTML =
    '<option value="">全部二级</option>' +
    uniq2.map((v) => `<option value="${escHtml(v)}">${escHtml(v)}</option>`).join("");
  if (filterL2 && uniq2.indexOf(filterL2) < 0) filterL2 = "";
  s2.value = filterL2;
}

function renderTree() {
  const box = $("pcat-keys");
  if (!box) return;
  const rows = filteredNodes();
  if (!rows.length) {
    box.innerHTML = "<p class='mc-desc'>没有匹配的淘宝叶子。未入树 CID 导入会阻断。</p>";
    return;
  }
  const groups = {};
  rows.forEach((n) => {
    const root = nodeL1(n) || "其它";
    if (!groups[root]) groups[root] = [];
    groups[root].push(n);
  });
  const blocks = Object.keys(groups)
    .sort()
    .map((root) => {
      const body = groups[root]
        .map((n) => {
          const cid = nodeCid(n);
          const active = selectedCid === cid ? " class=\"pcat-row-active\"" : "";
          return `<tr${active} data-cid="${escHtml(cid)}">
            <td><code>${escHtml(cid)}</code></td>
            <td>${escHtml(nodeL1(n) || "—")}</td>
            <td>${escHtml(nodeL2(n) || n.name || "—")}</td>
            <td>${escHtml(nodePath(n) || "—")}</td>
            <td>${escHtml(GENDER_ZH[n.gender] || n.gender || "—")}</td>
          </tr>`;
        })
        .join("");
      return `<h4 class="mc-subheading">${escHtml(root)} · ${groups[root].length}</h4>
        <table class="mcp-delivery-table pcat-table">
          <thead>
            <tr><th>淘宝 cid</th><th>一级</th><th>二级</th><th>路径</th><th>性别</th></tr>
          </thead>
          <tbody>${body}</tbody>
        </table>`;
    })
    .join("");
  box.innerHTML = `<p class="mc-desc">本表 ${rows.length} 片叶子 · 点行可高亮</p>${blocks}`;
  box.querySelectorAll("tbody tr[data-cid]").forEach((tr) => {
    tr.addEventListener("click", () => {
      const cid = tr.getAttribute("data-cid") || "";
      selectedCid = selectedCid === cid ? "" : cid;
      renderAll();
    });
  });
}

function renderAll() {
  renderMeta();
  renderLevelFilters();
  renderTree();
}

export async function loadCategoriesPanel() {
  const keysBox = $("pcat-keys");
  if (keysBox) keysBox.textContent = "加载中…";
  const { ok, data } = await apiPublic("GET", "/api/v1/platform/categories");
  if (!ok || !data) {
    notify((data && data.message) || "产品类别加载失败", false);
    if (keysBox) keysBox.innerHTML = "<p class='mc-desc'>加载失败，请刷新重试。</p>";
    return;
  }
  cache = data;
  renderAll();
}

export function initCategoriesHandlers() {
  bindClick("btn-pcat-refresh", () => loadCategoriesPanel());
  const s1 = $("pcat-filter-l1");
  if (s1) {
    s1.addEventListener("change", () => {
      filterL1 = s1.value || "";
      filterL2 = "";
      selectedCid = "";
      renderAll();
    });
  }
  const s2 = $("pcat-filter-l2");
  if (s2) {
    s2.addEventListener("change", () => {
      filterL2 = s2.value || "";
      selectedCid = "";
      renderAll();
    });
  }
  const search = $("pcat-search");
  if (search) {
    search.addEventListener("input", () => {
      searchText = search.value || "";
      renderAll();
    });
  }
}
